<template>
  <div class="home">
    <div class="home__toc d-flex flex-column justify-content-center">
      <div class="container">
        <div class="home__toc__container">
          <div class="home__toc__container__jumbotron">
            <img src="../assets/images/header.jpg" width="70%" />
            <h1 class="home__toc__container__jumbotron__heading mb-3">
              Pandora Papers: By the numbers
            </h1>
            <p class="home__toc__container__jumbotron__lead lead">
              Highlighting and visualizing a selection of the key data points found in the leak of more than 11.9 million files.
            </p>
            <sharing-options
              :url="$config.get('app.url')"
              class="home__toc__container__jumbotron__sharing" />
          </div>
          <div class="text-right my-3">
            <haptic-copy
              :text="chartsTsv"
              label="Copy list"
              class="btn-outline-secondary btn-sm" />
          </div>
          <div class="home__toc__container__items">
            <div v-for="{ id, anchor, title, isEmpty } in nonEmptyCharts" :key="id" class="home__toc__container__items__item">
              <a href="#" v-scroll-to="anchor" :class="{ 'text-muted': isEmpty }" class="d-block">
                <div class="home__toc__container__items__item__thumbnail">
                  <img
                    v-if="screenshots[id]"
                    class="home__toc__container__items__item__thumbnail__image"
                    :src="screenshots[id]" />
                  <fa
                    v-else
                    class="home__toc__container__items__item__thumbnail__placeholder"
                    icon="chart-bar" />
                </div>
                <div class="home__toc__container__items__item__title">
                  {{ title }}
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="home__charts">
      <div class="home__charts__toc">
        <bullets-toc :charts="charts" :screenshots="screenshots" class="m-2" />
      </div>
      <div v-for="({ component, chart, id, name, title }) in nonEmptyCharts" class="home__charts__wrapper" :key="id">
        <div class="container" :id="id">
          <div class="row">
            <div class="col my-2">
              <component
                :is="component"
                :chart="chart"
                :social-mode="socialModes[id]"
                :no-footer="noFooters[id]"
                class="home__charts__wrapper__component border" />
            </div>
            <div class="col-12 col-lg-5 home__charts__wrapper__embed my-2" v-if="showEmbeds">
              <b-tabs content-class="mt-2" no-fade>
                <b-tab title="Embed" active>
                  <embed-form
                    :url="chartAbsolutePath(name)"
                    :height="660"
                    no-preview
                    no-title
                    class="overflow-hidden mt-4" />
                  <b-form-checkbox v-model="socialModes[id]" class="mb-2">
                    <span class="font-weight-bold">Social mode</span> <span class="text-muted">(dark background, no footer)</span>
                  </b-form-checkbox>
                  <b-form-checkbox v-model="noFooters[id]">
                    <span class="font-weight-bold">No footer</span>
                  </b-form-checkbox>
                </b-tab>
                <b-tab title="Link">
                  <advanced-link-form
                    pills
                    :forms="['raw', 'rich', 'markdown']"
                    :title="title"
                    :link="chartAbsolutePath(name)" />
                </b-tab>
                <b-tab title="Download">
                  <download-form
                    :chart="chart"
                    :title="title"
                    :social-mode.sync="socialModes[id]"  />
                </b-tab>
                <template #tabs-end>
                  <b-nav-item :href="chartRelativePath(name)" target="_blank" class="ml-auto">
                    <fa icon="external-link-alt" class="mr-1" />
                    <span class="sr-only">
                      In a new window
                    </span>
                  </b-nav-item>
                </template>
              </b-tabs>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { find, filter, uniqueId } from 'lodash'
import { toJpeg } from 'html-to-image'
import { routes, ChartRoute } from '../router'

import BulletsToc from '../components/BulletsToc.vue'
import DownloadForm from '../components/DownloadForm.vue'

export default {
  name: 'Home',
  components: {
    BulletsToc,
    DownloadForm
  },
  data () {
    return {
      noFooters: {},
      screenshots: {},
      socialModes: {}
    }
  },
  async mounted () {
    await this.$nextTick()
    await new Promise(resolve => setTimeout(resolve, 1000))
    await this.screenshotAllCharts()
  },
  methods: {
    chartRelativePath (name) {
      const chart = find(routes, { name })
      const socialMode = !!this.socialModes[chart.id]
      const noFooter = !!this.noFooters[chart.id]
      const query = { socialMode, noFooter }
      const route = this.$router.resolve({ name, query })
      return route.href
    },
    chartAbsolutePath (name) {
      const base = 'https://projects.icij.org'
      return [base, this.chartRelativePath(name)].join('')
    },
    screenshotChart (id) {
      const selector = `#${id} .chart__main`
      const element = this.$el.querySelector(selector)
      try {
        return toJpeg(element)
      } catch (_) {
        return null
      }
    },
    async screenshotAllCharts () {
      for (const { id } of this.nonEmptyCharts) {
        const dataUrl = await this.screenshotChart(id)
        this.$set(this.screenshots, id, dataUrl)
      }
    }
  },
  computed: {
    charts () {
      return filter(routes, route => route instanceof ChartRoute)
    },
    nonEmptyCharts () {
      return filter(this.charts, route => !route.isEmpty)
    },
    chartsTsvHeader () {
      return ['title', 'url', 'iframe', 'responsive_iframe'].join('\t')
    },
    chartsTsv () {
      return routes.reduce((rows, route) => {
        if (route.chart && !route.isEmpty) {
          const url = `${this.$config.get('app.url')}/${route.name}`
          const iframe = `<iframe width="100%" height="600" src="${url}" frameborder="0" allowfullscreen></iframe>`
          const script = 'script'
          const chartId = uniqueId(`icij-chart-${route.name}-`)
          const responsiveIframe = `<${script} type="text/javascript" src="https://pym.nprapps.org/pym.v1.min.js"></${script}><div id="${chartId}"></div><${script}>;new pym.Parent("${chartId}", "${url}", {})</${script}>`
          rows.push([route.title, url, iframe, responsiveIframe].join('\t'))
        }
        return rows
      }, [this.chartsTsvHeader]).join('\n')
    },
    showEmbeds () {
      return this.$route.name !== 'Embed'
    }
  }
}
</script>

<style lang="scss">
  @import '../variables.scss';
  @import '../../node_modules/bootstrap/scss/_mixins.scss';

  .home {

    &__toc {
      min-height: 90vh;
      padding: 6rem 0;

      &__container {

        &__jumbotron {
          text-align: center;
          font-family: $project-font-family;
          margin-bottom:  $spacer-xl;

          &__heading {
            font-family: inherit;
          }

          &__sharing {
            position: relative;
            display: inline-flex !important;

            &:after, &:before {
              content: "";
              width: 3rem;
              height: 0.2rem;
              margin: 0 $spacer;
              background-color: $primary;
              position: absolute;
              top: 50%;
              transform: translateY(-50%);
            }

            &:before {
              right: 100%;
            }

            &:after {
              left: 100%;
            }
          }
        }

        &__items {
          display: grid;
          grid-template-columns: minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr);
          grid-template-rows: auto auto auto;
          grid-gap: $spacer-xxl;

          @include media-breakpoint-down(md) {
            grid-template-columns: minmax(100px, 1fr) minmax(100px, 1fr);
            grid-gap: $spacer-xl;
          }

          @include media-breakpoint-down(sm) {
            grid-template-columns: minmax(100px, 1fr);
            grid-gap: $spacer-lg;
          }

          &__item {
            display: inline-block;
            color: #000 !important;
            text-align: center;
            font-size: $font-size-sm;
            position: relative;
            border: $border-color 1px solid;

            &:hover {
              background: mix($primary, white, 10);
            }

            &__thumbnail {
              display: block;
              background: #fff;
              width: 100%;
              padding-top: 75%;
              position: relative;
              margin: 0 auto;
              overflow: hidden;

              &__image, &__placeholder {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
              }

              &__image {
                max-width: 100%;
                max-height: 100%;
              }

              &__placeholder {
                font-size: 1.5rem;
                color: $body-color !important;
                opacity: 0.2;
              }
            }

            &__title {
              position: absolute;
              bottom: 0px;
              left: 0px;
              right: 0px;
              padding: $spacer;
              font-weight: bold;
              display: none;
            }
          }
        }
      }
    }

    &__charts {
      background: #f9f9f9;

      &__toc {
        z-index: 1000;
        position: sticky;
        top: calc(50% - 75px);
        height: 0;
        text-align: right;
      }

      &__wrapper {

        &:last-of-type .container:after {
          display: none;
        }

        & .container {
          position: relative;
          padding-top: 6rem;
          padding-bottom: 6rem;

          &:after {
            content: "";
            height: 0.2rem;
            margin: 0 $spacer;
            background-color: $primary;
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
          }
        }

        &__embed {
          font-size: 0.9rem;

          label, .btn {
            font-size: inherit;
          }

          .tabs .nav-tabs .nav-link.active {
            background: #f9f9f9 !important;
            border-bottom-color: #f9f9f9;
            font-weight: bold;
          }

          .embed-form > .container-fluid {
            padding: 0;
          }
        }
      }
    }
  }
</style>
